import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { BaseService } from 'shared-lib';
import { CryptUtilService } from 'src/app/shared/services/crypt-util.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProjectSummaryService {
  public package_function_obj: BehaviorSubject<any> = new BehaviorSubject({});
  public featureData$: BehaviorSubject<any> = new BehaviorSubject([]);
  public projectInfoData$: BehaviorSubject<any> = new BehaviorSubject([]);
  public popup_flag: Subject<Boolean> = new Subject();
  public cr_tooltip_msg: BehaviorSubject<string> = new BehaviorSubject('');
  public isCRPending: boolean;
  public isDownload: BehaviorSubject<boolean>;
  public isRedirectionBtn: BehaviorSubject<boolean>;
  public tileDescription: BehaviorSubject<string> = new BehaviorSubject('');
  private userStoryRedirection: BehaviorSubject<boolean>;
  public featureList: BehaviorSubject<any> = new BehaviorSubject([]);
  // PMOne RAID - commented out
  // public raidSheetLogDetails: BehaviorSubject<any> = new BehaviorSubject([]);
  public projectSummaryInfoData$: Subject<any> = new Subject();
  jiraExportReadyDetails: BehaviorSubject<any> = new BehaviorSubject(null);

  constructor(private baseService: BaseService, private http: HttpClient, private cryptUtilService: CryptUtilService,
  ) {
    this.isDownload = new BehaviorSubject(false);
    this.isRedirectionBtn = new BehaviorSubject(false);
    this.userStoryRedirection = new BehaviorSubject(false);
    // PMOne RAID - commented out
    // this.raidSheetLogDetails = new BehaviorSubject([]);
  }

  setToolTipMsg(msg: string) {
    this.cr_tooltip_msg.next(msg);
  }

  getToolTipMsg(): Observable<string> {
    return this.cr_tooltip_msg as Observable<string>;
  }

  setUserStoryRedirection(isEnabled: boolean) {
    this.userStoryRedirection.next(isEnabled);
  }

  getUserStoryRedirection(): Observable<boolean> {
    return this.userStoryRedirection as Observable<boolean>;
  }

  setFeatureData(data) {
    this.featureData$.next(data);
  }

  getFeatureData(): Observable<any> {
    return this.featureData$.asObservable();
  }

  setProjectInfoData(data) {
    this.projectInfoData$.next(data);
  }

  getProjectInfoData(): Observable<any> {
    return this.projectInfoData$.asObservable();
  }

  setProjectSummaryInfoData(data: any) {
    this.projectSummaryInfoData$.next(data);
  }

  getProjectSummaryInfoData(): Observable<any> {
    return this.projectSummaryInfoData$.asObservable();
  }

  /**
   * SetJiraExportReadyDetails: Sets the details for Jira export readiness.
   * 
   * @param jiraExportReadyDetails 
   */
  setJiraExportReadyDetails = jiraExportReadyDetails =>
    this.jiraExportReadyDetails.next(jiraExportReadyDetails);

  /**
   * GetJiraExportReadyDetailsObservable: Returns an observable for Jira export readiness details.
   * 
   * @returns jiraExportReadyDetails Observable
   */
  getJiraExportReadyDetailsObservable = (): Observable<any> =>
    this.jiraExportReadyDetails?.asObservable();

  getFeatureList() {
    return this.http.get(environment.API_MICROSERVICE_URL?.ADMIN + '/package/features/0');
  }
  getSummaryMasterData(packageId: any, functionId: any, featureId: any, langId) {
    return this.http.get(`${environment.API_MICROSERVICE_URL?.ADMIN}/package/preferences/${packageId}/${functionId}/${featureId}?languageId=${langId}`);
  }

  getProjectInfo(projectId: any, params?: any, languageId = 1) {
    const isMasterProject = this.cryptUtilService.getItem('isMasterProject', 'LOCAL') || false;
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.http.get(apiUrl + `/project/` + projectId + `/${languageId}`, { params });
  }

  /**
   * Helper method to get the appropriate API URL based on isMasterProject flag
   */
  private getProjectApiUrl(isMasterProject: boolean): string {
    return isMasterProject 
      ? environment.API_MICROSERVICE_URL.VERSION.V2.PROJECT
      : environment.API_MICROSERVICE_URL.PROJECT;
  }


  getFilteredProjectInfo(projectId: any, params?: any, languageId = 1) {
    return this.http.get(environment.API_MICROSERVICE_URL?.PROJECT + `/project/filteredProjectDetails/` + projectId + `/${languageId}`, { params });
  }

  getRespectiveProjectInfo(projectId: any, params?: any, languageId = 1, BASE_URL = environment.API_MICROSERVICE_URL?.PROJECT) {
    return this.http.get(BASE_URL + `/project/` + projectId + `/${languageId}`, { params });
  }

  getProjectGenAiStatus(projectId: any) {
    return this.http.get(environment.API_MICROSERVICE_URL?.PROJECT + `/project/gen-ai-enable-status/` + projectId);
  }

  getProjectAutonomousDevStatus(projectId: any) {
    return this.http.get(environment.API_MICROSERVICE_URL?.PROJECT + `/project/autonomous-dev-enabled-status/` + projectId);
  }

  getProcessTaxonomy(projectId: any, packageId: any, functionId: any) {
    return this.http.get(`${environment.API_MICROSERVICE_URL?.PROJECT}/project/summary/process/${projectId}/${packageId}/${functionId}`);
  }

  //change according to multirelease
  getTaxonomyCountData(projectId: any) {
    const isMasterProject = this.cryptUtilService.getItem('isMasterProject', 'LOCAL') || false;
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.http.get(`${apiUrl}/project/summary/process-count-summary/${projectId}`);
  }
//change according to multirelease
  getProcessTaxonomySummary(projectId: any) {
    const isMasterProject = this.cryptUtilService.getItem('isMasterProject', 'LOCAL') || false;
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.http.get(`${apiUrl}/project/summary/process-taxonomy-summary/${projectId}`);
  }

  getProcessTaxonomyCreateProject(projectId: any, packageId: any, functionId: any, industryPrintId) {
    return this.http.get(`${environment.API_MICROSERVICE_URL?.PROJECT}/project/summary/process/${projectId}/${packageId}/${functionId}/${industryPrintId}`);
  }

  getSprintsDetail(projectId: any, packageId: any, functionId: any) {
    return this.http.get(`${environment.API_MICROSERVICE_URL?.PROJECT}/project/summary/sprints/${projectId}/${packageId}/${functionId}`);
  }

  getTestCaseTileData(projectId: any, packageId: any, functionId: any) {
    return this.http.get(`${environment.API_MICROSERVICE_URL?.PROJECT}/project/summary/testCases/${projectId}/${packageId}/${functionId}`);
  }

  getUserStoriesTileData(projectId: any, packageId: any, functionId: any) {
    return this.http.get(`${environment.API_MICROSERVICE_URL?.PROJECT}/project/summary/userStories/${projectId}/${packageId}/${functionId}`);
  }

  getMethodologyDetail(projectId: any, packageId: any, functionId: any, languageId: number) {
    let data = this.http.get(`${environment.API_MICROSERVICE_URL?.PROJECT}/project/summary/methodology/${projectId}/${packageId}/${functionId}/${languageId}`);
    return data;
  }
  getProjectStatusDetail(projectId: any, packageId: any, functionId: any) {
    let data = this.http.get(`${environment.API_MICROSERVICE_URL?.PROJECT}/project/summary/methodology/${projectId}/${packageId}/${functionId}`);
    return data;
  }

  getProjectMapData(projectId: any, packageId: any, functionId: any, languageId?: number) {
    if (languageId == undefined || languageId == null) {
      languageId = 1
    }
    return this.http.get(`${environment.API_MICROSERVICE_URL?.PROJECT}/project/summary/amplifiersDeliverables/${projectId}/${packageId}/${functionId}/${languageId}`);
  }

  getIsExportCompleted(project_id, taxonomyId?) {
    return this.http.get<any>(`${environment.API_MICROSERVICE_URL?.PROJECT}/sprint-planning/sprint-generation/sprints/isReadyToExport/${project_id}`);
  }

  getProjectTools(projectId) {
    return this.http.get(`${environment.API_MICROSERVICE_URL?.PROJECT}/project/summary/project-tools/${projectId}`);
  }

  getMsTeamsStatus(projectId) {
    return this.http.get(`${environment.API_MICROSERVICE_URL?.PROJECT}/tools/msteams-status/${projectId}`);
  }

  createGenAIRoot(projectId) {
    const url = `${environment.API_MICROSERVICE_URL?.PROJECT}/tools/create-genai-root/${projectId}`;
    return this.http.post(url, {});
  }

  setDownloadFlag(data) {
    this.isDownload.next(data);
  }

  getDownloadFlag(): Observable<any> {
    return this.isDownload.asObservable();
  }
  setTileDescription(desc: string) {
    this.tileDescription.next(desc);
  }

  getTileDescription(): Observable<string> {
    return this.tileDescription as Observable<string>;
  }

  getTraditionalAmplifierData(projectId, packageId, pageNumber, langId) {
    const url = `/project/dashboard/traditional-amplifiers/${projectId}/${packageId}?pageNumber=${pageNumber}&pageSize=${8}&languageId=${langId}`;

    return this.baseService.get(url, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  getMarketSolutionsData(projectId: any, packageId: number, pageNumber: number, languageId: number): Observable<any> {
    const url = `/project/dashboard/market-solutions/${projectId}/${packageId}?pageNumber=${pageNumber}&pageSize=${8}&languageId=${languageId}`;
    return this.baseService.get(url, environment.API_MICROSERVICE_URL?.PROJECT);
  }
  getGenAIAmplifier(projectId, packageId, pageNumber, langId) {
    const url = `/project/dashboard/genai-amplifiers/${projectId}/${packageId}?pageNumber=${pageNumber}&pageSize=${8}&languageId=${langId}`;
    return this.baseService.get(url, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  getConstructIds(projectId: number): Observable<any> {
    return this.baseService.get(`/project/pmg-construct-ids/${projectId}`, environment.API_MICROSERVICE_URL?.PROJECT)
  }

  setRedirection(data: boolean) {
    this.isRedirectionBtn.next(data);
  }

  getRedirection(): Observable<boolean> {
    return this.isRedirectionBtn as Observable<boolean>;
  }

  /**
   * GetPrimaryToolData: It'll return project & package specific
   * Primary tools (GenAI + Traditional Amplifier)
   *
   * @param projectId Number
   * @param packageId Number
   * @param langId Number
   * @returns Object
   */
  getPrimaryToolData = (
    projectId: number,
    packageId: number,
    langId: number
  ): Observable<any> => {
    const url = `/project/dashboard/primary-tools/${projectId}/${packageId}?languageId=${langId}`;
  
    return this.baseService.get(url, environment.API_MICROSERVICE_URL?.PROJECT);
  };

  /**
   * GetAdditionalToolData: It'll return project & package specific
   * Additional Tools (Traditional Amplifier + Market Solution)
   *
   * @param projectId Number
   * @param packageId Number
   * @param pageNumber Number
   * @param pageSize Number
   * @param langId Number
   * @returns Object
   */
  getAdditionalToolData = (
    projectId: number,
    packageId: number,
    pageNumber: number,
    pageSize: number,
    langId: number
  ): Observable<any> => {
    const url = `/project/dashboard/additional-tools/${projectId}/${packageId}?pageNumber=${pageNumber}&pageSize=${pageSize}&languageId=${langId}`;
  
    return this.baseService.get(url, environment.API_MICROSERVICE_URL?.PROJECT);
  };

   /**
   * GetDusCount: Method returns a count of dus table data
   * @param projectId string or number
   * @returns number
   */
   getDUSCount(projectId: number | string): Observable<any> {
    return this.http.get(
      `${environment.API_MICROSERVICE_URL?.PROJECT}/dus/duscount/${projectId}`
    );
  }
  
  /**
   * GetRaidLogSheetDetails: Method returns raid log sheet details.
   * 
   * @param {Integer} sheetId
   * @returns {Observable}
   */
  getRaidLogSheetDetails = (
    sheetId: number,
    projectId: string
  ): Observable<any> =>
    this.http.get(
      `${environment.API_MICROSERVICE_URL?.PROJECT}/tools/getRaidLogSheetDetails/${sheetId}/${projectId}`
    );
  
  /**
   * GetDeleteReasons: Fetch the list of reasons for release deletion
   * @returns {Observable} List of deletion reasons
   */
  getDeleteReasons(): Observable<any> {
    const url = `${environment.API_MICROSERVICE_URL?.PROJECT}/project/releases/getDeleteReasons`;
    return this.http.get(url);
  }

  /**
   * DeleteRelease: Delete a release with specified reason
   * @param {Object} payload - Contains releaseProjectId, reason, status, and emailId
   * @returns {Observable} Delete response
   */
  deleteRelease(payload: { releaseProjectId: number; reason: string; status: string; emailId: string }): Observable<any> {
    const url = `${environment.API_MICROSERVICE_URL?.PROJECT}/project/releases/deleteRelease`;
    return this.http.post(url, payload);
  }
  
}